import queryString from 'query-string';
import { validateEmail, isMobileDevice, isSkyInterruptedAuthError } from '../utils';

class SkyInterruptedAuthenticationError extends Error {
  constructor(message, code, customData) {
    super(message, code);
    this.code = code;
    this.customData = customData;
  }
}

/**
 * Servicios API
 *
 * @class ServiceApi
 */
class ServiceApi {

  constructor() {
    this.config = window.configOptions;
    this.SkyInterruptedAuthenticationError = SkyInterruptedAuthenticationError;
  }

  /**
   * Llama al endpoint para validar un login con usuario y contraseña.
   *
   * @param {*} username
   * @param {*} password
   * @param {*} parameters
   */
  userPassLogin = async (username, password, parameters, state, retry) => {
    const options = {
      grantType: 'password',
      countryCode: 'br',
      password,
      retry
    };
    const isMobile = isMobileDevice();
    if (validateEmail(username)) {
      options.email = username;
    } else {
      options.telephoneNumber = username;
    }
    const { recaptchaRef } = window;
    if (recaptchaRef && recaptchaRef.current) {
      options['g-recaptcha-response'] = await recaptchaRef.current
        .executeAsync()
        .catch((err) => console.error('Error loading captcha', err));
    }
    parameters.state = parameters.state || state;
    let headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf-8',
      'x-domain': 'vrioservices.com',
      'x-client-type': isMobile ? 'app' : 'web'
    };

    if (document.documentElement.lang && document.documentElement.lang !== '') {
      headers['Accept-Language'] = document.documentElement.lang;
    }

    const url = `${this.config.TBX_IDP_LOGINURL}/oauth2/authorize`;

    return fetch(url + '?' + queryString.stringify(parameters), {
      method: 'post',
      headers: headers,
      body: JSON.stringify(options)
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.error || response.status >= 400) {
          const error = response;
          if (isSkyInterruptedAuthError(error)) {
            throw new SkyInterruptedAuthenticationError(
              error.message,
              error.code,
              { interruptionModal: error.interruptionModal || error?.error?.interruptionModal }
            );
          } else {
            throw error;
          }
        }

        return response;
      }).finally(() => window.recaptchaRef && window.recaptchaRef.current && window.recaptchaRef.current.reset());
  }

  /**
   * Llama al endpoint para validar un login passwordless con usuario, contraseña y codigo ingresado.
   *
   * @param {*} username
   * @param {*} password
   * @param {*} parameters
   */
  userPassActivationCode = async (username, password, activationCode, parameters) => {
    const options = {
      grant_type: 'password',
      activationCode,
      username,
      password
    };
    await this.addRecaptchaHeader(options);
    let headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf-8'
    };

    if (document.documentElement.lang && document.documentElement.lang !== '') {
      headers['Accept-Language'] = document.documentElement.lang;
    }

    return fetch(this.config.IDP_ACTIVATION_CODE + '?' + queryString.stringify(parameters), {
      method: 'post',
      headers: headers,
      body: JSON.stringify(options)
    })
      .then((response) => {
        if (response.status >= 400) {
          return response.json()
            .catch(error => {
              throw new Error(response.statusText, response.status);
            })
            .then((error) => {
              switch (error && error.error_description) {
                case 'EXPIRATED_CODE':
                  throw new Error('ActivationCode.expiratedCode');
                case 'INVALID_ACTIVATION_CODE':
                  throw new Error('ActivationCode.invalidCode');
                default:
                  throw new Error('ActivationCode.defaultError');
              }
            });
        }
        return response.json();
      }).finally(() => window.recaptchaRef && window.recaptchaRef.current && window.recaptchaRef.current.reset());
  }

  /**
   * Obtain recaptcha configuration options and IDP list
   *
   * @param {Object} options
   * @param {string} options.clientId
   * @returns {Promise}
   */
  obtainRecaptchaConfig() {
    if (!this.config) {
      return Promise.resolve(null);
    }
    const savedRecaptcha = JSON.parse(sessionStorage.getItem('recaptchaDisabled'));
    if (savedRecaptcha) {
      return Promise.resolve(savedRecaptcha);
    }
    const url = this.config.TBX_IDP_RECAPTCHA_URL;

    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.error || response.status >= 400) {
          throw response.error;
        }
        sessionStorage.setItem('recaptchaDisabled', JSON.stringify(response));
        return response;
      });
  }

  addRecaptchaHeader = async (options) => {
    const { recaptchaRef } = window;
    if (recaptchaRef && recaptchaRef.current) {
      options['g-recaptcha-response'] = await recaptchaRef.current.executeAsync()
        .catch((err) => console.error('Error loading captcha', err));
    }
  }

  createOtpCode = async (parameters) => {
    let options = {};
    const isMobile = isMobileDevice();
    const { recaptchaRef } = window;
    const { username } = parameters;
    if (recaptchaRef && recaptchaRef.current) {
      options['g-recaptcha-response'] = await recaptchaRef.current
        .executeAsync()
        .catch((err) => console.error('Error loading captcha', err));
    }

    if (validateEmail(username)) {
      options.email = username;
    } else {
      options.telephoneNumber = username;
    }

    let headers = {
      Accept: 'application/json',
      'x-client-type': isMobile ? 'app' : 'web',
      'x-domain': 'vrioservices.com',
      'Content-Type': 'application/json; charset=utf-8'
    };

    if (document.documentElement.lang && document.documentElement.lang !== '') {
      headers['Accept-Language'] = document.documentElement.lang;
    }

    return fetch(
      `${this.config.TBX_IDP_LOGINURL}/oauth2/code`, {
      method: 'POST',
      headers,
      body: JSON.stringify(options)
    }).then((response) => response.json())
      .then((response) => {
        if (response.error || response.status >= 400) {
          const error = response;
          if (isSkyInterruptedAuthError(error)) {
            throw new SkyInterruptedAuthenticationError(
              error.message,
              error.code,
              { interruptionModal: error.interruptionModal || error?.error?.interruptionModal }
            );
          } else {
            throw response;
          }
        }

        return response;
      }).finally(() => window.recaptchaRef && window.recaptchaRef.current && window.recaptchaRef.current.reset());
  }

  authenticate = async (parameters) => {
    let options = {};
    let reqQuery = '';
    const isMobile = isMobileDevice();
    const { recaptchaRef } = window;
    const { username, password, activationCode, createCode, parameters: queryParams } = parameters;
    if (recaptchaRef && recaptchaRef.current) {
      options['g-recaptcha-response'] = await recaptchaRef.current
        .executeAsync()
        .catch((err) => console.error('Error loading captcha', err));
    }

    options.grantType = activationCode ? 'passwordless' : 'password';

    if (options.grantType === 'password') {
      options.password = password;
    }

    if (options.grantType === 'passwordless') {
      options.activationCode = activationCode;
    }

    if (createCode) {
      reqQuery = '?' + queryString.stringify({ createCode: true, redirect_uri: queryParams.redirect_uri });
    }


    if (validateEmail(username)) {
      options.email = username;
    } else {
      options.telephoneNumber = username;
    }

    let headers = {
      Accept: 'application/json',
      'x-client-type': isMobile ? 'app' : 'web',
      'x-domain': 'vrioservices.com',
      'Content-Type': 'application/json; charset=utf-8'
    };

    if (document.documentElement.lang && document.documentElement.lang !== '') {
      headers['Accept-Language'] = document.documentElement.lang;
    }

    return fetch(
      `${this.config.TBX_IDP_LOGINURL}/oauth2/authenticate` + reqQuery, {
      method: 'POST',
      headers,
      body: JSON.stringify(options)
    }).then((response) => response.json())
      .then((response) => {
        if (response.error || response.status >= 400) {
          const error = response;
          if (isSkyInterruptedAuthError(error)) {
            throw new SkyInterruptedAuthenticationError(
              error.message,
              error.code,
              { interruptionModal: error.interruptionModal || error?.error?.interruptionModal }
            );
          } else {
            throw response;
          }
        }

        return response;
      }).finally(() => window.recaptchaRef && window.recaptchaRef.current && window.recaptchaRef.current.reset());
  }
}

export default ServiceApi;
